import {
  hasOpaqueLinearLightBackground,
  isLinearLightBlendMode,
} from '../../utils/linearLight/source';
/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled from 'styled-components';
import {
  useCombinedRefs,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import { _x } from '@web-stories-wp/i18n';
import {
  StoryAnimation,
  STORY_ANIMATION_STATE,
  useStoryAnimationContext,
} from '@web-stories-wp/animation';

/**
 * Internal dependencies
 */
import {
  useStory,
  useCanvas,
  useCanvasBoundingBoxRef,
  CANVAS_BOUNDING_BOX_IDS,
} from '../../app';
import StoryPropTypes from '../../types';
import DisplayElement from './displayElement';
import { hasElementBackdrop } from '../../elements/shared/getElementBackdropStyles';
import { Layer, PageArea } from './layout';
import { STABLE_ARRAY } from '../../constants';
import { isEqual } from '../../../../../utils/isEqual';

const DisplayPageArea = styled(PageArea)`
  position: absolute;
`;

function DisplayPage({
  pageElements,
  opaqueBackdrop,
  animationState,
  editingElement,
  resetAnimationState,
  selectedElementIds,
}) {
  const {
    actions: { WAAPIAnimationMethods },
  } = useStoryAnimationContext();

  useEffect(() => {
    switch (animationState) {
      case STORY_ANIMATION_STATE.PLAYING_SELECTED:
      case STORY_ANIMATION_STATE.PLAYING:
        WAAPIAnimationMethods.play();
        return;

      case STORY_ANIMATION_STATE.RESET:
        WAAPIAnimationMethods.reset();
        return;

      case STORY_ANIMATION_STATE.SCRUBBING:
        /*
         * See `../../../dashboard/components/previewPage: 82` for reference
         *
         * @todo
         * - have WAAPIAnimationMethods.setCurrentTime(time) subscribe to time setter
         * - return an unsubscribe function when state changes.
         */
        WAAPIAnimationMethods.pause();
        return;

      case STORY_ANIMATION_STATE.PAUSED:
        WAAPIAnimationMethods.pause();
        return;
      default:
        return;
    }
  }, [animationState, WAAPIAnimationMethods]);

  /*
   * Reset animation state if user changes page
   */
  useEffect(() => resetAnimationState, [resetAnimationState, pageElements]);

  return pageElements
    ? pageElements.map((element) => {
        if (
          editingElement === element.id &&
          (!isLinearLightBlendMode(element.blendMode) ||
            hasElementBackdrop(element))
        ) {
          return null;
        }
        if (element.isHidden) {
          return null;
        }

        const shouldPlayElementAnimation =
          animationState === STORY_ANIMATION_STATE.PLAYING ||
          (animationState === STORY_ANIMATION_STATE.PLAYING_SELECTED &&
            selectedElementIds.includes(element.id));

        return (
          <DisplayElement
            key={element.id}
            element={element}
            opaqueBackdrop={opaqueBackdrop}
            isEditing={editingElement === element.id}
            isAnimatable
            shouldPlayElementAnimation={shouldPlayElementAnimation}
          />
        );
      })
    : null;
}

DisplayPage.propTypes = {
  pageElements: PropTypes.arrayOf(StoryPropTypes.element),
  opaqueBackdrop: PropTypes.bool,
  animationState: PropTypes.oneOf(Object.values(STORY_ANIMATION_STATE)),
  editingElement: StoryPropTypes.element,
  resetAnimationState: PropTypes.func,
  selectedElementIds: PropTypes.arrayOf(PropTypes.string),
};

function DisplayLayer() {
  const {
    // Have page elements shallowly equaled for scenarios like animation
    // updates where elements don't change, but we get a new page elements
    // array
    currentPageElements,
  } = useStory(({ state }) => {
    return {
      currentPageElements: state.currentPage?.elements || STABLE_ARRAY,
    };
  });

  const {
    currentPageAnimations,
    currentPageBackgroundColor,
    animationState,
    updateAnimationState,
    updateRemotionAnimations,
    selectedElements,
  } = useStory(({ state, actions }) => {
    return {
      currentPageAnimations: state.currentPage?.animations || STABLE_ARRAY,
      currentPageBackgroundColor: state.currentPage?.backgroundColor || null,
      animationState: state.animationState,
      selectedElements: state.selectedElements,
      updateAnimationState: actions.updateAnimationState,
      updateRemotionAnimations: actions.updateRemotionAnimations,
    };
  });

  const boundingBoxTrackingRef = useCanvasBoundingBoxRef(
    CANVAS_BOUNDING_BOX_IDS.PAGE_CONTAINER
  );

  const { editingElement, setPageContainer, setFullbleedContainer } = useCanvas(
    ({
      state: { editingElement },
      actions: { setPageContainer, setFullbleedContainer },
    }) => ({ editingElement, setPageContainer, setFullbleedContainer })
  );

  const isBackgroundSelected =
    selectedElements?.[0]?.id === currentPageElements?.[0]?.id;

  const resetAnimationState = useCallback(() => {
    updateAnimationState({ animationState: STORY_ANIMATION_STATE.RESET });
  }, [updateAnimationState]);

  const animatedElements = useMemo(
    () => selectedElements.map((el) => el.id),
    [selectedElements]
  );

  const strippedElementsRef = useRef({
    elements: STABLE_ARRAY,
    map: new Map(),
  });

  const currentPageElementsWithoutNonAnimatableProps = useMemo(() => {
    // Preserve referential equality for animatable elements so non-animatable
    // updates (e.g. isMuted) don't churn StoryAnimation context.
    const previous = strippedElementsRef.current;
    const previousElements = previous.elements;
    const previousElementsMap = previous.map;
    const nextElementsMap = new Map();
    let didChange = currentPageElements.length !== previousElements.length;

    const nextElements = currentPageElements.map((element, index) => {
      let nextElement = element;

      if (element.resource) {
        const { isMuted, ...resource } = element.resource;
        nextElement = {
          ...element,
          resource,
        };
      }

      const previousElement = previousElementsMap.get(element.id);
      if (previousElement && isEqual(previousElement, nextElement)) {
        nextElementsMap.set(element.id, previousElement);
        if (previousElements[index] !== previousElement) {
          didChange = true;
        }
        return previousElement;
      }

      nextElementsMap.set(element.id, nextElement);
      didChange = true;
      return nextElement;
    });

    if (!didChange) {
      return previousElements;
    }

    strippedElementsRef.current = {
      elements: nextElements,
      map: nextElementsMap,
    };

    return nextElements;
  }, [currentPageElements]);

  return (
    <StoryAnimation.Provider
      animations={currentPageAnimations}
      elements={currentPageElementsWithoutNonAnimatableProps}
      onWAAPIFinish={resetAnimationState}
      selectedElementIds={
        animationState === STORY_ANIMATION_STATE.PLAYING_SELECTED
          ? animatedElements
          : []
      }
      updateRemotionAnimations={updateRemotionAnimations}
    >
      <Layer
        data-testid="DisplayLayer"
        pointerEvents="none"
        aria-label={_x('Display layer', 'compound noun', 'web-stories')}
      >
        <DisplayPageArea
          data-linear-light-elements={JSON.stringify(
            currentPageElements?.map(({ id }) => id) || []
          )}
          ref={useCombinedRefs(setPageContainer, boundingBoxTrackingRef)}
          fullbleedRef={setFullbleedContainer}
          background={currentPageBackgroundColor}
          isBackgroundSelected={isBackgroundSelected}
          isControlled
        >
          <DisplayPage
            pageElements={currentPageElements}
            opaqueBackdrop={hasOpaqueLinearLightBackground(
              currentPageBackgroundColor
            )}
            editingElement={editingElement}
            animationState={animationState}
            resetAnimationState={resetAnimationState}
            selectedElementIds={animatedElements}
          />
        </DisplayPageArea>
      </Layer>
    </StoryAnimation.Provider>
  );
}

export default memo(DisplayLayer);
